import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "react-toastify";

import Meta from "../../components/Meta";
import api from "../../utils/axiosInstance";
import { getApiErrorMessage } from "../../utils/errorMessages";

import "./AdminLegalScreen.css";

const LEGAL_SLUGS = Object.freeze([
  "terms-and-conditions",
  "privacy-policy",
  "cookie-policy",
]);

function safeText(value = "") {
  return String(value ?? "").trim();
}

function formatDate(value = "") {
  if (!value) return "Not set";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "Not set";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(date);
}

export default function AdminLegalEditScreen() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    slug: safeText(slug),
    status: "published",
    body: "",
    lastUpdated: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const { data } = await api.get(`/admin/legal/${encodeURIComponent(slug)}`);
      const page = data?.page || {};

      setForm({
        title: page.title || "",
        slug: page.slug || safeText(slug),
        status: page.status || "published",
        body: page.body || "",
        lastUpdated: page.lastUpdated || "",
      });
    } catch (err) {
      const message = getApiErrorMessage(err, {
        fallback:
          "We could not load this legal page right now. Please try again later.",
      });

      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const update = useCallback((key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const savePage = useCallback(
    async (event) => {
      event.preventDefault();

      setSaving(true);
      setError("");

      try {
        const { data } = await api.put(
          `/admin/legal/${encodeURIComponent(slug)}`,
          {
            title: form.title,
            slug: form.slug,
            status: form.status,
            body: form.body,
          },
        );

        const page = data?.page || {};

        toast.success(data?.message || "Legal page updated.");

        if (page.slug && page.slug !== slug) {
          navigate(`/admin/legal/${encodeURIComponent(page.slug)}/edit`, {
            replace: true,
          });
          return;
        }

        setForm((prev) => ({
          ...prev,
          lastUpdated: page.lastUpdated || prev.lastUpdated,
        }));
      } catch (err) {
        const message = getApiErrorMessage(err, {
          fallback:
            "We could not save this legal page right now. Please try again later.",
        });

        setError(message);
        toast.error(message);
      } finally {
        setSaving(false);
      }
    },
    [form.body, form.slug, form.status, form.title, navigate, slug],
  );

  return (
    <main className="admin-legal">
      <Meta title="Edit legal page | Admin | Academy Costumes London" robots="noindex" />

      <Link to="/admin/legal" className="admin-legal__back">
        <ArrowLeft size={16} />
        Legal pages
      </Link>

      <header className="admin-legal__hero">
        <div>
          <p>Content</p>
          <h1>{form.title || "Legal page"}</h1>
          <span>Last updated {formatDate(form.lastUpdated)}</span>
        </div>
      </header>

      {error ? <p className="admin-legal__notice">{error}</p> : null}

      {loading ? (
        <section className="admin-legal__state">
          <Loader2 className="admin-legal__spin" />
          <p>Loading legal page...</p>
        </section>
      ) : (
        <form className="admin-legal__form" onSubmit={savePage}>
          <label>
            <span>Title</span>
            <input
              value={form.title}
              onChange={(event) => update("title", event.target.value)}
              required
              maxLength={160}
            />
          </label>

          <label>
            <span>Slug</span>
            <select
              value={form.slug}
              onChange={(event) => update("slug", event.target.value)}
              required
            >
              {LEGAL_SLUGS.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Status</span>
            <select
              value={form.status}
              onChange={(event) => update("status", event.target.value)}
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </select>
          </label>

          <label className="admin-legal__body-field">
            <span>Body</span>
            <textarea
              value={form.body}
              onChange={(event) => update("body", event.target.value)}
              required
              rows={22}
            />
          </label>

          <footer className="admin-legal__form-actions">
            <button
              type="submit"
              className="admin-legal__button"
              disabled={saving}
            >
              {saving ? (
                <Loader2 size={16} className="admin-legal__spin" />
              ) : (
                <Save size={16} />
              )}
              Save changes
            </button>
          </footer>
        </form>
      )}
    </main>
  );
}
